import React from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { Checkbox, Icon } from '@fikasio/react-ui-components';
import LinkifiedText from 'components/UI/LinkifiedText';
import TrashIcon from 'components/UI/TrashIcon';
import ProjectTag from 'components/projects/ProjectTag';
import OBJECTIVE_STATUS_OPTIONS from 'components/objectives/objectiveStatusOptions';
import AssigneeButton from 'components/tasks/TasksViewer/AssigneeButton';
import TaskStatusDropdown from 'components/tasks/TasksViewer/TaskStatusDropdown';
import { statusChangePatch, visibleStatus } from 'components/tasks/TasksViewer/statusChange';
import DropdownToggle from 'components/UI/DropdownToggle';
import {
  useAddObjectiveMutation,
  useDeleteObjectiveMutation,
  usePatchObjectiveMutation,
} from 'services/objectives/api';
import { Objective } from 'services/objectives/types';
import { RangeSelectEvent } from 'utils/rangeSelection';
import { formatShortDueDate } from 'utils/date';
import {
  formatObjectiveProgress,
  getObjectiveStatusColor,
  getProgressRatio,
  isClosedObjectiveStatus,
  isObjectiveBehind,
  objectiveStatus,
} from './utils';
import styles from './ObjectivesBoard.module.css';

export interface ObjectiveCardProps {
  isSelected?: boolean;
  objective: Objective;
  onClick: (objectiveId: string) => void;
  onSelect: (objective: Objective, event?: RangeSelectEvent) => void;
}

const ObjectiveCard = ({
  isSelected,
  objective,
  onClick,
  onSelect,
}: ObjectiveCardProps) => {
  const { t } = useTranslation();
  const [createObjective] = useAddObjectiveMutation();
  const [patchObjective] = usePatchObjectiveMutation();
  const [deleteObjective] = useDeleteObjectiveMutation();

  const dueDate = objective.dueDate ? DateTime.fromISO(objective.dueDate) : null;
  const status = objectiveStatus(objective);
  const isLate = isObjectiveBehind(objective);
  const color = getObjectiveStatusColor(objective);
  const progressRatio = getProgressRatio(objective);

  return (
    <article
      className={classNames(styles.card, {
        [styles.cardCompleted]: isClosedObjectiveStatus(status),
        [styles.cardSelected]: isSelected,
      })}
      onClick={event => {
        if (event.shiftKey) {
          event.preventDefault();
          onSelect(objective, event);
          return;
        }
        onClick(objective.id);
      }}
      onMouseDown={event => {
        if (event.shiftKey) {
          event.preventDefault();
        }
      }}
    >
      <div className={styles.header}>
        <div
          className={styles.checkbox}
          onClick={event => {
            event.stopPropagation();
            onSelect(objective, event);
          }}
          onMouseDown={event => {
            if (event.shiftKey) {
              event.preventDefault();
            }
          }}
        >
          <Checkbox isChecked={isSelected} />
        </div>
        <div
          className={styles.status}
          onClick={event => event.stopPropagation()}
        >
          <TaskStatusDropdown
            alignToggle="start"
            onChange={(nextStatus, extras) => {
              patchObjective({
                id: objective.id,
                ...statusChangePatch(nextStatus, {
                  cancelReason: extras?.cancelReason,
                  wasArchived: objective.isArchived,
                }),
              });
            }}
            status={visibleStatus(status, objective.isArchived)}
            statusOptions={OBJECTIVE_STATUS_OPTIONS}
          />
        </div>
        <div className={styles.title}>
          <span className={styles.name}>{objective.name}</span>
        </div>
        <div
          className={styles.actions}
          onClick={event => event.stopPropagation()}
        >
          <Dropdown
            style={{
              position: 'static',
            }}
          >
            <Dropdown.Toggle as={DropdownToggle} />
            <Dropdown.Menu>
              <Dropdown.Item
                onClick={() => createObjective(objective)}
              >
                <Icon
                  name="copy"
                  style={{
                    marginRight: 10,
                    width: 25,
                  }}
                />
                {t('duplicate')}
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => deleteObjective(objective.id)}
              >
                <TrashIcon
                  style={{
                    color: 'red',
                    marginRight: 10,
                    width: 25,
                  }}
                />
                {t('delete')}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
      {
        objective.description && (
          <div className={styles.description}>
            <LinkifiedText text={objective.description} />
          </div>
        )
      }
      {
        !!objective.projects?.length && (
          <div
            className={styles.projects}
            onClick={event => event.stopPropagation()}
          >
            {
              objective.projects.map(project => (
                <ProjectTag
                  key={project.id}
                  projectId={project.id}
                />
              ))
            }
          </div>
        )
      }
      <div className={styles.progress}>
        <div className={styles.progressBar}>
          <div
            className={styles.progressFill}
            style={{
              backgroundColor: color,
              width: `${progressRatio * 100}%`,
            }}
          />
        </div>
        <div className={styles.progressLabel}>
          {formatObjectiveProgress(objective, t)}
        </div>
      </div>
      <div className={styles.footer}>
        <span
          className={classNames(styles.deadline, {
            [styles.deadlineLate]: isLate,
          })}
        >
          {
            dueDate?.isValid
              ? formatShortDueDate(dueDate.toJSDate())
              : '-'
          }
        </span>
        <div className={styles.footerMeta}>
          {
            (!objective.goalKind || objective.goalKind === 'REACH')
            && objective.goalFrequency && (
              <span className={styles.frequency}>
                {t(objective.goalFrequency === 'DAILY' ? 'daily' : 'weekly')}
              </span>
            )
          }
          <div onClick={event => event.stopPropagation()}>
            <AssigneeButton
              assigneeId={objective.assignee || undefined}
              onChange={assignee => {
                patchObjective({
                  assignee,
                  id: objective.id,
                });
              }}
            />
          </div>
        </div>
      </div>
    </article>
  );
};

export default ObjectiveCard;
